import { motion } from "framer-motion";
import { Clock, MapPin, TramFront } from "lucide-react";
import SectionReveal from "./SectionReveal";

const BUILDING =
  "https://customer-assets.emergentagent.com/job_5adba607-3cb4-4890-a176-530c0c902f8d/artifacts/kkkl20b5_imgi_1_IMG_1653-1-scaled.jpg";
const MARKUS_PHOTO =
  "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/unh624pd_processed_homepage_photo.PNG";

const HOURS = [
  ["Di – Sa", "09:00 — 12:00"],
  ["Di – Fr", "15:00 — 18:00"],
  ["So / Mo", "geschlossen"],
];

const TEAM = [
  {
    name: "Markus Zürcher",
    role: "Inhaber · Meisterhand seit Jahrzehnten",
    rotate: "-rotate-2",
    photo: MARKUS_PHOTO,
  },
];

export default function Team() {
  return (
    <section
      id="casa"
      className="section relative bg-[var(--cream-alt)] overflow-hidden"
      data-testid="casa-section"
    >
      <div className="container-wide">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: Building image */}
          <SectionReveal className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <motion.img
                src={BUILDING}
                alt="Casa Markus — Hauptstrasse 74, Binningen"
                initial={{ scale: 1.1 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-[var(--cream)] mix-blend-difference">
                <div>
                  <p className="overline !text-white">Casa Markus Zürcher</p>
                  <p className="font-serif text-xl mt-2">Hauptstrasse 74 · Binningen</p>
                </div>
                <span className="font-serif text-5xl italic">1949</span>
              </div>
            </div>
          </SectionReveal>

          {/* Right: Team + Info */}
          <div className="lg:col-span-6">
            <SectionReveal>
              <span className="overline">Casa Markus</span>
              <h2 className="h-display text-5xl md:text-6xl lg:text-7xl mt-6 text-[var(--anthracite)]">
                Besuchen Sie uns in der{" "}
                <span className="italic-accent text-[var(--sage-dark)]">Casa.</span>
              </h2>
              <p className="mt-6 text-base md:text-lg text-[var(--muted-fg)] leading-relaxed max-w-md">
                Markus Zürcher empfängt Sie persönlich in der Werkstatt. Nur{" "}
                <strong className="text-[var(--anthracite)]">6 Minuten zu Fuss</strong> vom Zoo
                Dorenbach-Kreisel entfernt.
              </p>
              <div className="mt-6 flex items-center gap-3 text-[var(--anthracite)]">
                <TramFront size={18} strokeWidth={1.4} className="text-[var(--sage-dark)]" />
                <span className="text-sm">Tram Nr. 2 · Binningen Kronenplatz</span>
              </div>
            </SectionReveal>

            {/* Team photo — clean, straight */}
            <div className="mt-12">
              {TEAM.map((m, i) => (
                <SectionReveal key={m.name} delay={i}>
                  <div className="max-w-sm" data-testid={`team-card-${i}`}>
                    <div className="aspect-square overflow-hidden bg-[var(--cream-alt)] relative">
                      <img
                        src={m.photo}
                        alt={m.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                    <div className="pt-5">
                      <p className="font-serif text-2xl text-[var(--anthracite)] leading-tight">
                        {m.name}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted-fg)] mt-2">
                        {m.role}
                      </p>
                    </div>
                  </div>
                </SectionReveal>
              ))}
            </div>

            {/* Opening hours + address */}
            <div className="mt-16 grid sm:grid-cols-2 gap-8 border-t border-[var(--border)] pt-12">
              <SectionReveal>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={14} strokeWidth={1.5} className="text-[var(--sage-dark)]" />
                  <span className="overline text-[10px]">Anfahrt</span>
                </div>
                <p className="font-serif text-2xl text-[var(--anthracite)] leading-tight">
                  Hauptstrasse 74
                  <br />
                  4102 Binningen
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted-fg)] mt-3">
                  bei der Hohle Gasse
                </p>
                <a
                  href="https://maps.google.com/?q=Hauptstrasse+74+4102+Binningen"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-4 text-[11px] uppercase tracking-[0.22em] border-b border-[var(--anthracite)] pb-1 hover:text-[var(--sage-dark)] hover:border-[var(--sage-dark)] transition-colors"
                  data-testid="casa-maps-link"
                >
                  Route auf Google Maps
                </a>
              </SectionReveal>
              <SectionReveal delay={1}>
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={14} strokeWidth={1.5} className="text-[var(--sage-dark)]" />
                  <span className="overline text-[10px]">Öffnungszeiten</span>
                </div>
                <dl className="space-y-2 font-sans text-sm">
                  {HOURS.map(([day, time], i) => (
                    <div key={i} className="flex justify-between gap-4 border-b border-dashed border-[var(--border)] pb-2">
                      <dt className="text-[var(--anthracite)] font-medium w-20">{day}</dt>
                      <dd className="text-[var(--muted-fg)]">{time}</dd>
                    </div>
                  ))}
                </dl>
              </SectionReveal>
            </div>
          </div>
        </div>

        {/* Google Maps embed */}
        <SectionReveal className="mt-24">
          <div className="relative aspect-[16/7] overflow-hidden border border-[var(--border)]">
            <iframe
              title="Casa Markus Standort"
              src="https://www.google.com/maps?q=Hauptstrasse+74+4102+Binningen&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0"
              data-testid="google-maps-embed"
            />
          </div>
        </SectionReveal>
      </div>
    </section>
  );
}
